import { Box, SelectChangeEvent, Typography } from "@mui/material";
import { fabric } from "fabric";
import { observer } from "mobx-react-lite";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
// import { EditIcon2 } from "../../../../../assets/images/icons/EditIcon2.tsx";
import { debounce } from "lodash";
import BriaButton from "../../../../../components/common/BriaButton/BriaButton.tsx";
import BriaDropDown from "../../../../../components/common/BriaDropDown/BriaDropDown";
import BriaInput from "../../../../../components/common/BriaInput/BriaInput.tsx";
import { useAppStore } from "../../../../../hooks/useStores";
import { isAdsEditor } from "../../../../../utils/index.ts";
import useDesignEditorUtils, { FeatureType } from "../../../../CustomUtils/UseDesignEditor";
import Icons from "../../../../components/Icons";
import WizardImageHeader from "../../../../components/common/WizardImageHeader/WizardImageHeader.tsx";
import { PanelType } from "../../../../constants/app-options";
import { PopUpSteps } from "../../../../constants/mock-data.ts";
import { DesignEditorContext } from "../../../../contexts/DesignEditor.tsx";
import UseAdsEditorUtils, { logoConfigLabel } from "../../../../hooks/useAdsEditorUtils.tsx";
import useAppContext from "../../../../hooks/useAppContext";
import { useContextMenuRequest } from "../../../../hooks/useContextMenuRequest";
import useDesignEditorContext from "../../../../hooks/useDesignEditorContext";
import { useEditor } from "../../../../hooks/useEditor";
import useSetIsSidebarOpen from "../../../../hooks/useSetIsSidebarOpen.tsx";
import useSmartImageHeader from "../../../../hooks/useSmartImageHeader.tsx";
import { LayerType } from "../../../../types";
import { findGroupByElementId } from "../../utils/smartImageUtils";
import useSceneOperations from "../Footer/Graphic/Scenes/utils/SceneOperations";
import { ObjectsEnum } from "../Panels/panelItems";
import styles from "./ContextMenu.module.scss";
import ContextMenuItem from "./ContextMenuItem";
import { ContextMenuData, ICON_SIZE, OBJECT_TYPES } from "./ContextMenusData";

type Position = { left: number; top: number };

interface PanelConfig {
	panels: PanelType[];
}

function ContextMenu() {
	const PAN_SENSITIVITY = 0.2;
	let targetPan = { x: 0, y: 0 };
	let currentPan = { x: 0, y: 0 };
	let isAnimating = false;
	const [originalTop, setOriginalTop] = useState<string>("30px");
	const [subMenuAlign, setSubMenuAlign] = useState<string>("left");
	const { setContextMenuTimelineRequest, contextMenuTimelineRequest } = useDesignEditorContext();
	const editor = useEditor();
	const { campaignStore, designEditorStore } = useAppStore();
	const contextMenuRequest = useContextMenuRequest();
	const { isPopupView } = useContext(DesignEditorContext);
	const { t } = useTranslation("translation", { keyPrefix: "campaignTab.dropDownActions" });
	const ContextData = ContextMenuData(t, editor, campaignStore.isBrandTemplateEditor, campaignStore.isEditingAllAds);
	const [activeObject, setActiveObject] = useState(editor?.canvas?.canvas.getActiveObject()) as any;
	const [activeObjectLocked, setActiveObjectLocked] = useState(activeObject?.lockMovementX);
	const [contextMenuPosition, setContextMenuPosition] = useState<Position>({
		left: activeObject?.left ?? 0,
		top: activeObject?.top ?? 0,
	});
	const [contextMenuOpen, setContextMenuOpen] = useState<boolean>(false);
	const [isContextMoving, setIsContextMoving] = useState<boolean>(false);
	const [componentStatus, setComponentStatus] = useState("");
	const [items, setItems] = useState<any>([]);
	const [isObjectInGroup, setIsObjectInGroup] = useState<boolean>(false);
	const [state, setState] = useState({ isGroup: false, isMultiple: false });
	const { EditAllAds, fitTextIntoFixedTextBox } = useDesignEditorUtils();
	const { activePanel } = useAppContext();
	const { deleteScene, duplicateScene } = useSceneOperations();
	const { getFabricLayerTakenConfigs, getFirstAvailableConfig } = UseAdsEditorUtils();
	const { handleDeleteSmartImage } = useSmartImageHeader();
	const WIZARD_IMAGE_INDEX = PopUpSteps.indexOf("WizardImages");
	const setIsSidebarOpen = useSetIsSidebarOpen();

	useEffect(() => {
		handleUpdateActiveObjectAndPosition();
		setContextMenuOpen(false);
		const canvas = editor?.canvas?.canvas;
		if (activeObject) {
			setState({
				isGroup: activeObject.type === "group",
				isMultiple: activeObject.type === "activeSelection",
			});

			// Check if active object is inside a group
			if (canvas && activeObject.id) {
				const parentGroup = findGroupByElementId(canvas, activeObject.id);
				setIsObjectInGroup(!!parentGroup);
			} else {
				setIsObjectInGroup(false);
			}

			const isStaticText =
				activeObject?.name === OBJECT_TYPES.STATIC_TEXT ||
				activeObject?.type === OBJECT_TYPES.STATIC_TEXT ||
				activeObject?._objects?.[0]?.type === OBJECT_TYPES.STATIC_TEXT;
			const isStaticImage =
				activeObject?.name === OBJECT_TYPES.STATIC_IMAGE ||
				activeObject?.type === OBJECT_TYPES.STATIC_IMAGE ||
				activeObject?._objects?.[0]?.type === OBJECT_TYPES.STATIC_IMAGE;
			const isStaticImageLogo =
				(activeObject?.name === OBJECT_TYPES.STATIC_IMAGE ||
					activeObject?.type === OBJECT_TYPES.STATIC_IMAGE ||
					activeObject?._objects?.[0]?.type === OBJECT_TYPES.STATIC_IMAGE) &&
				activeObject?.isLogo;

			setComponentStatus(
				isStaticText
					? activeObject?.textType || activeObject?._objects?.[0]?.textType
					: isStaticImageLogo
						? activeObject?.logoConfigType || activeObject?._objects?.[0]?.logoConfigType
						: isStaticImage
							? activeObject?.imageType || activeObject?._objects?.[0]?.imageType
							: activeObject?.shapeType || activeObject?._objects?.[0]?.shapeType,
			);
			updateMenuItems();
		}

		if (canvas) {
			canvas.on("object:moving", handleObjectMoving);
			canvas.on("text:changed", handleObjectEditing);
			canvas.on("text:changed", debouncedEditAllAdsText);
			canvas.on("object:modified", handleUpdateActiveObjectAndPosition);
			canvas.on("object:scaling", handleUpdateActiveObjectAndPosition);
			window.addEventListener("resize", handleUpdateActiveObjectAndPosition);
		}
		const panelMap: Partial<Record<LayerType, PanelConfig>> = {
			[LayerType.STATIC_TEXT]: {
				panels: [PanelType.SHAPEFILLCOLOR, PanelType.RESIZE, PanelType.CANVASCOLOR, PanelType.STROKECOLOR],
			},
			[LayerType.STATIC_VECTOR]: {
				panels: [
					PanelType.RESIZE,
					PanelType.CANVASCOLOR,
					PanelType.TEXTFILL,
					PanelType.FONTSELECTOR,
					PanelType.STROKECOLOR,
					PanelType.TEXTHIGHLIGHTER,
				],
			},
			[LayerType.STATIC_CIRCLE]: {
				panels: [
					PanelType.RESIZE,
					PanelType.CANVASCOLOR,
					PanelType.TEXTFILL,
					PanelType.FONTSELECTOR,
					PanelType.TEXTHIGHLIGHTER,
					PanelType.STROKECOLOR,
				],
			},
			[LayerType.STATIC_RECT]: {
				panels: [
					PanelType.RESIZE,
					PanelType.CANVASCOLOR,
					PanelType.TEXTFILL,
					PanelType.FONTSELECTOR,
					PanelType.TEXTHIGHLIGHTER,
					PanelType.STROKECOLOR,
				],
			},
			[LayerType.STATIC_TRIANGLE]: {
				panels: [
					PanelType.RESIZE,
					PanelType.CANVASCOLOR,
					PanelType.TEXTFILL,
					PanelType.FONTSELECTOR,
					PanelType.TEXTHIGHLIGHTER,
					PanelType.STROKECOLOR,
				],
			},
			[LayerType.STATIC_IMAGE]: {
				panels: [
					PanelType.RESIZE,
					PanelType.CANVASCOLOR,
					PanelType.TEXTFILL,
					PanelType.FONTSELECTOR,
					PanelType.TEXTHIGHLIGHTER,
					PanelType.SHAPEFILLCOLOR,
					PanelType.STROKECOLOR,
				],
			},
			[LayerType.STATIC_PATH]: {
				panels: [
					PanelType.RESIZE,
					PanelType.CANVASCOLOR,
					PanelType.TEXTFILL,
					PanelType.FONTSELECTOR,
					PanelType.TEXTHIGHLIGHTER,
					PanelType.SHAPEFILLCOLOR,
					PanelType.STROKECOLOR,
				],
			},
			[LayerType.FRAME]: {
				panels: [
					PanelType.TEXTFILL,
					PanelType.FONTSELECTOR,
					PanelType.TEXTHIGHLIGHTER,
					PanelType.SHAPEFILLCOLOR,
					PanelType.STROKECOLOR,
				],
			},
		};

		if (activeObject?.type && activeObject.type in panelMap && !isPopupView) {
			const layerType = activeObject.type as LayerType;
			const panelConfig = panelMap[layerType];
			if (panelConfig && panelConfig.panels.includes(activePanel)) {
				setIsSidebarOpen(false);
			}
		}

		return () => {
			if (canvas) {
				canvas.off("object:moving", handleObjectMoving);
				canvas.off("text:changed", handleObjectEditing);
				canvas.off("text:changed", debouncedEditAllAdsText);
				debouncedEditAllAdsText.cancel();
				canvas.off("object:modified", handleUpdateActiveObjectAndPosition);
				canvas.off("object:scaling", handleUpdateActiveObjectAndPosition);
				window.removeEventListener("resize", handleUpdateActiveObjectAndPosition);
			}
		};
	}, [activeObject, editor?.canvas?.canvas.getActiveObject()]);

	const animatePan = () => {
		if (!isAnimating) return;

		currentPan.x += (targetPan.x - currentPan.x) * 0.2;
		currentPan.y += (targetPan.y - currentPan.y) * 0.2;

		editor?.canvas.canvas.relativePan(new fabric.Point(currentPan.x, currentPan.y));

		if (Math.abs(targetPan.x - currentPan.x) > 0.5 || Math.abs(targetPan.y - currentPan.y) > 0.5) {
			requestAnimationFrame(animatePan);
		} else {
			isAnimating = false;
		}
	};
	// Handle two-finger trackpad panning by setting isPanning
	// and moving the canvas using Fabric.js relativePan.
	editor?.canvas.canvas.wrapperEl.addEventListener(
		"wheel",
		(event: WheelEvent) => {
			// Only handle pixel-based scrolling (trackpads)
			const isTrackpadGesture =
				event.deltaMode === 0 && Math.abs(event.deltaX) > 0 && !event.ctrlKey && !event.metaKey;

			if (isTrackpadGesture) {
				event.preventDefault();

				targetPan.x = -event.deltaX * PAN_SENSITIVITY;
				targetPan.y = -event.deltaY * PAN_SENSITIVITY;

				if (!isAnimating) {
					isAnimating = true;
					currentPan = { x: 0, y: 0 };
					animatePan();
				}
			}
		},
		{ passive: false },
	);

	useEffect(() => {
		const isSmartImageSelected =
			activeObject?.name === OBJECT_TYPES.GROUP &&
			activeObject._objects.length >= 2 &&
			activeObject._objects.some((obj: fabric.Object) => obj.id === ObjectsEnum.SmartImage);
		designEditorStore.setProperty("isSmartImageObjectSelected", isSmartImageSelected);

		editor?.canvas?.canvas.on("text:editing:entered", async function () {
			if (isPopupView && designEditorStore.activeStep != PopUpSteps.indexOf("text")) {
				activeObject.exitEditing();
			}
		});

		return () => {
			if (editor?.canvas?.canvas) {
				editor?.canvas?.canvas.off("text:editing:entered");
			}
		};
	}, [activeObject]);

	useEffect(() => {
		let watcher = async () => {
			const active = editor?.canvas?.canvas.getActiveObject();
			setActiveObjectLocked(active?.lockMovementX);
			if (activeObject) {
				setState({
					isGroup: activeObject.type === "group",
					isMultiple: activeObject.type === "activeSelection",
				});
			}
		};
		if (editor) {
			editor.on("history:changed", watcher);
		}
		return () => {
			if (editor) {
				editor.off("history:changed", watcher);
			}
		};
	}, [editor, activeObject]);

	const handleEditAllAdsText = (e: any) => {
		const modifiedObject = e.target;
		if (modifiedObject?.type === "StaticText") {
			const newTextValue = modifiedObject.text;
			EditAllAds(LayerType.STATIC_TEXT, activeObject.textType, FeatureType.TEXT, newTextValue);
		}
	};

	const debouncedEditAllAdsText = debounce(handleEditAllAdsText, 450);

	const reAssignTheFixedWidthAndHeightToTheActiveObject = (active: fabric.Object) => {
		if (
			active &&
			active?.type === LayerType.STATIC_TEXT &&
			active.fixedSize &&
			active.fixedWidthValue &&
			active.fixedHeightValue &&
			active.width &&
			active.height &&
			active.fontSize
		) {
			if (active.width > active.fixedWidthValue) {
				active.set("width", active.fixedWidthValue);
			}
			if (active.height > active.fixedHeightValue) {
				active.set("height", active.fixedHeightValue);
			}
			active.set("width", active.fixedWidthValue);
			active.set("height", active.fixedHeightValue);
		}
	};

	const handleObjectMoving = () => {
		if (isPopupView) return;
		const active = editor?.canvas?.canvas.getActiveObject();
		if (active) {
			const activeObject: fabric.Object = active;
			reAssignTheFixedWidthAndHeightToTheActiveObject(activeObject);
		}
		setIsContextMoving(true);
	};

	const handleUpdateActiveObjectAndPosition = () => {
		setIsContextMoving(false);
		const active: any = editor?.canvas?.canvas.getActiveObject();
		setActiveObject(active);
		// Ensure the configuration menu is fully visible and not clipped

		if (active?.type === LayerType.FRAME) {
			const menuWidth = 270;
			const padding = 10;
			const canvasRect = editor?.canvas.canvas.wrapperEl.getBoundingClientRect();
			const windowWidth = window.innerWidth;

			// Base canvas-relative position
			let canvasLeft = active?.lineCoords?.tr?.x;
			let canvasTop = active?.lineCoords?.tl?.y;

			// Convert canvas-relative to window-relative
			const windowLeft = canvasRect?.left + canvasLeft;

			// Check for overflow and adjust canvas-relative left
			if (windowLeft + menuWidth > windowWidth - padding) {
				canvasLeft -= windowLeft + menuWidth - (windowWidth - padding);
			}
			if (windowLeft < padding) {
				canvasLeft += padding - windowLeft;
			}

			setContextMenuPosition({ left: canvasLeft, top: canvasTop });
		} else {
			setContextMenuPosition({ left: active?.lineCoords?.tr?.x ?? 0, top: active?.lineCoords?.tl?.y ?? 0 });
		}

		if (activeObject?.type === LayerType.STATIC_TEXT && activeObject?.fixedSize) {
			activeObject?.setControlsVisibility({
				mt: false,
				mb: false,
				ml: false,
				mr: false,
				bl: false,
				br: false,
				tl: false,
				tr: false,
				mtr: false,
			});
		}

		// Dynamically update menu position , so the menu will be always visible
		const canvas = editor?.canvas?.canvas;
		const originalCanvasHeight = editor?.scene.exportToJSON().frame?.height;

		const displayedCanvasHeight = canvas?.height;
		const originalCanvasWidth = editor?.scene.exportToJSON().frame?.width;
		const displayedCanvasWidth = canvas?.width;

		if (displayedCanvasHeight && originalCanvasHeight && displayedCanvasWidth && originalCanvasWidth) {
			const scaleFactorY = displayedCanvasHeight / originalCanvasHeight;
			const scaleFactorX = displayedCanvasWidth / originalCanvasWidth;
			const _currentScene = editor.scene.exportToJSON();

			for (const layer of _currentScene.layers) {
				if (layer?.width && layer?.scaleX && layer !== undefined && activeObject?.id === layer.id) {
					const layerTop = layer?.top;
					const top = layerTop && layerTop * scaleFactorY;
					const layerLeft = layer?.left;
					const left = layerLeft && layerLeft * scaleFactorX;
					const width = layer?.width * layer?.scaleX;
					const canvasRect = editor?.canvas?.canvas?.getElement()?.getBoundingClientRect();

					// Calculate the object's top position relative to the viewport
					const objectTopInViewport = top && canvasRect?.top + top;
					if (objectTopInViewport && objectTopInViewport > 290) {
						setOriginalTop("-100px");
					} else {
						setOriginalTop("30px");
					}
					if (width && left && left + width < displayedCanvasWidth / 2) {
						setSubMenuAlign("left");
					} else {
						setSubMenuAlign("right");
					}
				}
			}
		}
		reAssignTheFixedWidthAndHeightToTheActiveObject(active);
	};
	const handleObjectEditing = () => {
		const active = editor?.canvas?.canvas.getActiveObject();
		if (active) {
			const activeObject: fabric.Object = active;
			reAssignTheFixedWidthAndHeightToTheActiveObject(activeObject);
			setActiveObject(activeObject);
			if (
				activeObject?.type === LayerType.STATIC_TEXT &&
				activeObject.fixedSize &&
				activeObject.fixedWidthValue &&
				activeObject.fixedHeightValue
			) {
				(activeObject as fabric.Textbox).styles = {}; // Copy-paste breaks fitting, this is necessary for that issue
				fitTextIntoFixedTextBox(activeObject);
				activeObject.height = activeObject.fixedHeightValue;
			}
		}
	};
	const showContextMenu = () => {
		editor?.cancelContextMenuRequest();
		setContextMenuOpen(!contextMenuOpen);
	};

	const renderMenuItems = (menuItems: any[]) => {
		return menuItems.map((item, index) => (
			<Box key={index}>
				{item.divider ? (
					<hr className={styles.divider} />
				) : (
					<ContextMenuItem
						disabled={item.disabled}
						onClick={item.onClick}
						icon={item.icon}
						label={item.label}
						isSubMenu={item.isSubMenu}
						iconOnly={item.iconOnly}
						Selected={item.Selected}
					>
						{item.children && (
							<div
								className={`${styles.subMenu} ${
									subMenuAlign === "left" ? styles.alignLeft : styles.alignRight
								}`}
							>
								{renderMenuItems(item.children)}
							</div>
						)}
					</ContextMenuItem>
				)}
			</Box>
		));
	};

	const renderContextMenu = (menuItems: any[]) => {
		return (
			<Box
				onContextMenu={(e) => e.preventDefault()}
				className={styles.contextMenu}
				style={{
					top: contextMenuRequest ? `${contextMenuRequest.top}px` : originalTop,
					left: `${contextMenuRequest ? contextMenuRequest.left : -132}px`,
				}}
			>
				{renderMenuItems(menuItems)}
			</Box>
		);
	};
	const renderContextMenuItems = () => {
		if (!contextMenuRequest || !contextMenuRequest.target || isPopupView) {
			return null;
		}
		if (
			contextMenuRequest.target.type === OBJECT_TYPES.FRAME ||
			contextMenuRequest.target.type === OBJECT_TYPES.INITIAL_FRAME
		) {
			return renderContextMenu(ContextData.backgroundMenu);
		} else if (
			contextMenuRequest.target.id === ObjectsEnum.InnerRectangle ||
			contextMenuRequest.target.id === ObjectsEnum.OuterRectangle ||
			contextMenuRequest.target.id === ObjectsEnum.OriginalImage ||
			contextMenuRequest.target.id === ObjectsEnum.SmartImage
		) {
			return renderContextMenu(ContextData.smartImageMenu);
		}
		if (contextMenuOpen) {
			setContextMenuOpen(false);
		}
		return renderContextMenu(ContextData.mainMenu);
	};

	const handleComponentStatusChange = (e: SelectChangeEvent<string>) => {
		const value = e.target.value;
		setComponentStatus(value);

		if (activeObject.name === LayerType.STATIC_TEXT || activeObject.type === LayerType.STATIC_TEXT) {
			activeObject.textType = value;
		} else if (
			(activeObject.name === LayerType.STATIC_IMAGE || activeObject.type === LayerType.STATIC_IMAGE) &&
			activeObject?.isLogo
		) {
			activeObject.logoConfigType = value;
		} else if (activeObject.name === LayerType.STATIC_IMAGE || activeObject.type === LayerType.STATIC_IMAGE) {
			activeObject.imageType = value;
		} else {
			activeObject.shapeType = value;
		}
		updateMenuItems();
		editor?.history.save();
	};
	const updateMenuItems = () => {
		const currentScene = editor?.scene.exportToJSON();
		let usedConfigs: Set<string> | undefined = undefined;

		if (currentScene) {
			usedConfigs = getFabricLayerTakenConfigs(currentScene?.layers, activeObject.type, activeObject?.isLogo);
		}

		const menu = isStaticText
			? ContextData.textComponentStatusMenu
			: isStaticImageLogo
				? ContextData.logoComponentStatusMenu
				: isStaticImage
					? ContextData.imageComponentStatusMenu
					: isFabricShape
						? ContextData.basicShapeComponentStatusMenu
						: ContextData.shapeComponentStatusMenu;

		const updatedMenu = menu.map((item) => ({
			...item,
			disabled: usedConfigs?.has(item.value) ?? false,
		}));

		setItems(updatedMenu);
	};

	const isStaticText =
		activeObject?.name === OBJECT_TYPES.STATIC_TEXT ||
		activeObject?.type === OBJECT_TYPES.STATIC_TEXT ||
		activeObject?._objects?.[0]?.type === OBJECT_TYPES.STATIC_TEXT;
	const isStaticImage =
		activeObject?.name === OBJECT_TYPES.STATIC_IMAGE ||
		activeObject?.type === OBJECT_TYPES.STATIC_IMAGE ||
		activeObject?._objects?.[0]?.type === OBJECT_TYPES.STATIC_IMAGE;
	const isStaticImageLogo =
		(activeObject?.name === OBJECT_TYPES.STATIC_IMAGE ||
			activeObject?.type === OBJECT_TYPES.STATIC_IMAGE ||
			activeObject?._objects?.[0]?.type === OBJECT_TYPES.STATIC_IMAGE) &&
		activeObject?.isLogo;
	const isFabricShape =
		activeObject?.type === LayerType.STATIC_RECT ||
		activeObject?.type === LayerType.STATIC_CIRCLE ||
		activeObject?.type === LayerType.STATIC_TRIANGLE;
	const isSmartImageSelected =
		activeObject?.name === OBJECT_TYPES.GROUP &&
		activeObject._objects.length >= 2 &&
		activeObject._objects.some((obj: fabric.Object) => obj.id === ObjectsEnum.SmartImage);

	const isInnerRectangleSelected =
		activeObject?.id === ObjectsEnum.InnerRectangle ||
		(activeObject?.name === OBJECT_TYPES.GROUP &&
			activeObject._objects.length >= 2 &&
			activeObject._objects.some((obj: fabric.Object) => obj.id === ObjectsEnum.InnerRectangle));

	const isOuterRectangleSelected =
		activeObject?.id === ObjectsEnum.OuterRectangle ||
		(activeObject?.name === OBJECT_TYPES.GROUP &&
			activeObject._objects.length >= 2 &&
			activeObject._objects.some((obj: fabric.Object) => obj.id === ObjectsEnum.OuterRectangle));

	return (
		<>
			{!isContextMoving && activeObject && !isPopupView && (
				<Box
					onContextMenu={(e) => e.preventDefault()}
					className={styles.actionsMenu}
					style={{
						top: `calc(${contextMenuPosition?.top}px - 45px)`,
						left:
							activeObject.type === OBJECT_TYPES.STATIC_VECTOR ||
							activeObject.type === OBJECT_TYPES.FRAME ||
							activeObject.name === OBJECT_TYPES.GROUP
								? `calc(${contextMenuPosition.left}px - 100px)`
								: !isAdsEditor()
									? `calc(${contextMenuPosition.left}px - 270px)`
									: `calc(${contextMenuPosition.left}px - 100px)`,
					}}
				>
					{/* we hide FixedTextBoxSizeCheckBox for now and we will add it in future with an updated design  */}
					{/* {activeObject.type === LayerType.STATIC_TEXT && <FixedTextBoxSizeCheckBox />} */}
					{!isObjectInGroup && !isAdsEditor() && (
						<Typography className={styles.configureLabel}>{t("configuration")}</Typography>
					)}
					{/* Group/Ungroup button - hide if group contains inner or outer rectangle */}
					{!(
						activeObject?.name === OBJECT_TYPES.GROUP &&
						activeObject._objects &&
						activeObject._objects.length >= 2 &&
						activeObject._objects.some(
							(obj: fabric.Object) =>
								obj.id === ObjectsEnum.InnerRectangle || obj.id === ObjectsEnum.OuterRectangle,
						)
					) &&
						(state.isGroup ? (
							<BriaButton
								buttonType="textMedium"
								className={styles.groupButton}
								onClick={() => {
									editor?.objects.ungroup();
									setState({ ...state, isGroup: false });
								}}
							>
								{t("ungroup")}
							</BriaButton>
						) : state.isMultiple ? (
							<BriaButton
								buttonType="textMedium"
								className={styles.groupButton}
								onClick={() => {
									editor?.objects.group();
									setState({ ...state, isGroup: true });
								}}
							>
								{t("group")}
							</BriaButton>
						) : null)}
					{!isObjectInGroup &&
						(activeObject.type === OBJECT_TYPES.FRAME ||
							isSmartImageSelected ||
							isInnerRectangleSelected ||
							isOuterRectangleSelected) &&
						!isAdsEditor() && (
							<BriaInput
								rootClass={styles.rootClass}
								className={styles.inputField}
								height="20px"
								value={
									isSmartImageSelected
										? t("smartImage")
										: isOuterRectangleSelected
											? t("smartImageBoundary")
											: isInnerRectangleSelected
												? t("mainObject")
												: t("canvas")
								}
								onChange={() => {}}
								disabled
							/>
						)}
					{!isObjectInGroup &&
						activeObject.type !== OBJECT_TYPES.FRAME &&
						activeObject.type !== LayerType.STATIC_PATH &&
						activeObject.name !== OBJECT_TYPES.GROUP &&
						activeObject.type !== "activeSelection" &&
						!isAdsEditor() && (
							<BriaDropDown
								value={componentStatus}
								onChange={handleComponentStatusChange}
								items={items}
								height="20px"
								width="170px"
								className={
									activeObject.type === LayerType.STATIC_TEXT
										? styles.staticTextComponentStatus
										: styles.componentStatus
								}
								renderValue={(selected) => {
									if (selected) {
										return <Typography className={styles.renderedValue}>{selected}</Typography>;
									}
								}}
								placeholder={
									activeObject.type === OBJECT_TYPES.STATIC_TEXT
										? t("configureHeadingLevel")
										: activeObject.type === OBJECT_TYPES.STATIC_IMAGE
											? t("configureImageLevel")
											: activeObject.type === OBJECT_TYPES.STATIC_VECTOR
												? t("configureShapeLevel")
												: t("configureBasicShapeLevel")
								}
							/>
						)}
					{!isObjectInGroup && (
						<>
							<ContextMenuItem
								onClick={async () => {
									// @ts-ignore
									if (activeObjectLocked) {
										editor?.objects.unlock();
										editor?.cancelContextMenuRequest();
									} else {
										editor?.objects.lock();
										editor?.cancelContextMenuRequest();
									}
								}}
								icon={
									activeObjectLocked ? (
										<Icons.Locked size={ICON_SIZE} />
									) : (
										<Icons.Unlocked size={ICON_SIZE} />
									)
								}
								iconOnly={true}
							/>

							{!isOuterRectangleSelected &&
								!isInnerRectangleSelected &&
								activeObject?.id !== ObjectsEnum.FRAME && (
									<ContextMenuItem
										onClick={async () => {
											if (activeObject?.type == OBJECT_TYPES.FRAME) {
												if (!contextMenuTimelineRequest.id && editor) {
													setContextMenuTimelineRequest({
														...contextMenuTimelineRequest,
														id: editor?.scene.exportToJSON().id,
													});
												}
												await duplicateScene(editor?.scene.exportToJSON().id);
											} else {
												const currentScene = editor?.scene.exportToJSON();
												let usedConfigs: Set<string> | undefined = undefined;
												let firstAvailableConfig = undefined;
												if (currentScene && !isAdsEditor()) {
													usedConfigs = getFabricLayerTakenConfigs(
														currentScene?.layers,
														activeObject.type,
														activeObject?.isLogo,
													);
													firstAvailableConfig = getFirstAvailableConfig(
														usedConfigs,
														activeObject.type,
														activeObject?.isLogo ? logoConfigLabel : "",
													);
													if (!firstAvailableConfig) {
														campaignStore.setProperty(
															"nonAvailableConfigSnackbarOpen",
															true,
														);
														return;
													}
												}
												await editor?.objects.copy();
												editor?.objects.paste(firstAvailableConfig);
												editor?.cancelContextMenuRequest();
											}
										}}
										icon={<Icons.Duplicate size={ICON_SIZE} />}
										iconOnly={true}
									/>
								)}
							<ContextMenuItem
								onClick={async () => {
									if (activeObject?.type == OBJECT_TYPES.FRAME) {
										if (!contextMenuTimelineRequest.id && editor) {
											setContextMenuTimelineRequest({
												...contextMenuTimelineRequest,
												id: editor?.scene.exportToJSON().id, // Replace with the current scene ID
											});
										}
										await deleteScene(editor?.scene.exportToJSON().id);
									} else {
										const activeObject = editor?.canvas.canvas.getActiveObject();

										const isRectangleObject = (object: fabric.Object) =>
											[
												ObjectsEnum.InnerRectangle as string,
												ObjectsEnum.OuterRectangle as string,
											].includes(object.id);

										if (activeObject?.name === LayerType.GROUP.toLowerCase()) {
											activeObject._objects?.some((item) => {
												if (isRectangleObject(item)) {
													handleDeleteSmartImage();
													return true;
												}
												return false;
											});
										} else if (activeObject && isRectangleObject(activeObject)) {
											await handleDeleteSmartImage();
											return;
										}

										editor?.objects.remove();
										editor?.cancelContextMenuRequest();
									}
								}}
								icon={<Icons.Delete size={ICON_SIZE} />}
								iconOnly={true}
							/>
							{activeObject.type !== OBJECT_TYPES.FRAME &&
								activeObject.type !== OBJECT_TYPES.INITIAL_FRAME &&
								!isInnerRectangleSelected &&
								!isOuterRectangleSelected && (
									<ContextMenuItem
										onClick={showContextMenu}
										icon={<Icons.Dots size={ICON_SIZE} />}
										iconOnly={true}
									>
										{contextMenuOpen && renderContextMenu(ContextData.mainMenu)}
									</ContextMenuItem>
								)}
							{(isInnerRectangleSelected || isOuterRectangleSelected) && (
								<ContextMenuItem
									onClick={showContextMenu}
									icon={<Icons.Dots size={ICON_SIZE} />}
									iconOnly={true}
								>
									{contextMenuOpen && renderContextMenu(ContextData.smartImageMenu)}
								</ContextMenuItem>
							)}
						</>
					)}
				</Box>
			)}

			{isPopupView &&
				activeObject?.type === LayerType.STATIC_IMAGE &&
				designEditorStore.activeStep === WIZARD_IMAGE_INDEX &&
				!activeObject?.isLogo && (
					<WizardImageHeader contextMenuPosition={contextMenuPosition} showGenerateButton={!isPopupView} />
				)}
			{renderContextMenuItems()}
		</>
	);
}

const ObservedComponent = observer(ContextMenu);
export default ObservedComponent;
